import React__default, { Component } from 'react';
import { __extends } from '../../_virtual/_tslib.js';
import { debounce } from 'lodash';

var ScrollContext = React__default.createContext({
  scrollToTarget: function scrollToTarget() {},
  isScrolledToBottom: true
});
var ScrollConsumer = ScrollContext.Consumer;

var Scrollable =
/** @class */
function (_super) {
  __extends(Scrollable, _super);

  function Scrollable(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      isScrolledToBottom: true,
      hasInitialScrollCompleted: false,
      scrollBottom: 0,
      scrollTop: 0
    };
    _this.addScrollListener = _this.addScrollListener.bind(_this);
    _this.removeScrollListener = _this.removeScrollListener.bind(_this);
    /**
     *  Only update the `isScrolledToBottom` state at most once per animation frame, but
     *  fire an event on initial change to kick off the state change. This ensures that the state will be appropriately set when the waypoint is entered
     */

    _this.handleScroll = debounce(_this.handleScroll.bind(_this), 0, {
      leading: true
    });
    /**
     *  Only reset scroll position at most once per animation frame, but
     * - Make sure the first event is fired to reposition before the waypoint/fetchMore is fired again
     * - Make sure the last event is fired to capture the most up-to-date scrollHeight
     */

    _this.scrollToTarget = debounce(_this.scrollToTarget.bind(_this), 0, {
      trailing: true,
      leading: true
    });
    _this.scrollToBottom = _this.scrollToBottom.bind(_this);
    _this.scrollToReset = _this.scrollToReset.bind(_this);
    return _this;
  }

  Scrollable.prototype.componentDidMount = function () {
    if (this.props.element.current) {
      this.addScrollListener(this.props.element.current);
    }
  };

  Scrollable.prototype.componentDidUpdate = function (prevProps) {
    if (this.props.conversationId !== prevProps.conversationId) {
      this.scrollToBottom();
    }
    /**
     * HACK: The `element` ref is mutable and can change out from underneath us without the actual `current` reference
     *   changing. Thus, we have to blindly replace the scroll listener every time the component is updated. This is
     *   horribly inefficient in cases where the element has not in fact changed.
     *
     * TODO: Replace this whole <Scrollable> implementation with something more robust that doesn't rely on trying to
     *   observe the lifecycle of a DOM ref via React. Something like this looks promising, at least as inspiration:
     *   https://www.npmjs.com/package/react-stay-scrolled
     */


    if (this.props.element.current) {
      this.addScrollListener(this.props.element.current);
    }

    if (prevProps.element.current && prevProps.element.current !== this.props.element.current) {
      this.removeScrollListener(prevProps.element.current);
    }
  };

  Scrollable.prototype.componentWillUnmount = function () {
    this.props.element.current && this.removeScrollListener(this.props.element.current);
  }; // TODO: Use RAF to improve perf


  Scrollable.prototype.handleScroll = function (_event) {
    var element = this.props.element;

    if (this.state.hasInitialScrollCompleted && element.current) {
      var newScrollBottom = this.getScrollBottom();
      /**
       *  Can only set isScrolledToBottom = true if the user scrolled up.
       *  Otherwise, there is a race condition where isScrolledBottom is set to true after new messages load, but before it
       *  has been scrolled to the bottom again
       */

      var didUserScroll = this.state.scrollTop > element.current.scrollTop;
      var isScrolledToBottom = newScrollBottom === 0;
      this.setState({
        isScrolledToBottom: didUserScroll ? isScrolledToBottom : this.state.isScrolledToBottom,
        scrollBottom: newScrollBottom,
        scrollTop: element.current.scrollTop
      });
    }
  };

  Scrollable.prototype.addScrollListener = function (element) {
    element.addEventListener('scroll', this.handleScroll.bind(this));
  };

  Scrollable.prototype.removeScrollListener = function (element) {
    element.removeEventListener('scroll', this.handleScroll.bind(this));
  };

  Scrollable.prototype.getScrollBottom = function () {
    var element = this.props.element.current;

    if (element) {
      return element.scrollHeight - (element.scrollTop + element.clientHeight);
    }
  };

  Scrollable.prototype.scrollToBottom = function () {
    var element = this.props.element.current;

    if (element) {
      element.scrollTop = element.scrollHeight;
      this.setState({
        isScrolledToBottom: true,
        hasInitialScrollCompleted: true
      });
    }
  };

  Scrollable.prototype.scrollToReset = function () {
    var element = this.props.element.current;

    if (element) {
      element.scrollTop = element.scrollHeight - (element.clientHeight + this.state.scrollBottom);
    }
  };

  Scrollable.prototype.scrollToTarget = function () {
    if (this.state.isScrolledToBottom) {
      this.scrollToBottom();
    } else {
      this.scrollToReset();
    }
  };

  Scrollable.prototype.render = function () {
    return React__default.createElement(ScrollContext.Provider, {
      value: {
        scrollToTarget: this.scrollToTarget,
        isScrolledToBottom: this.state.isScrolledToBottom
      }
    }, this.props.children);
  };

  return Scrollable;
}(Component);

export default Scrollable;
export { ScrollConsumer, ScrollContext };
//# sourceMappingURL=index.js.map
